import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, Sparkles, FileText, CheckCircle2, Clock3 } from "lucide-react";
import { Button } from "@foundation/components/ui/button";
import { Card, CardContent } from "@foundation/components/ui/card";
import { useUser } from "@foundation/contextapi/UserContext";
import RevisrLayout from "../components/RevisrLayout";
import { PendingInvitePopup } from "@foundation/components/invitation/PendingInvitePopup";
import { policyTeableService } from "../services/policyTeableService";
import { getActiveWorkspaceId } from "../../../header/activeWorkspace";

type PolicyMetrics = {
  total: number;
  draft: number;
  inProgress: number;
  completed: number;
};

const INITIAL_POLICY_METRICS: PolicyMetrics = {
  total: 0,
  draft: 0,
  inProgress: 0,
  completed: 0,
};

export default function Dashboard() {
  const { user, isLoading: userIsLoading } = useUser();
  const navigate = useNavigate();
  const [policyMetrics, setPolicyMetrics] = useState<PolicyMetrics>(INITIAL_POLICY_METRICS);
  const [isMetricsLoading, setIsMetricsLoading] = useState<boolean>(false);

  // Navigation handler - go to workflow selection page
  const handleStartWorkflow = () => {
    navigate("/revisr/boost-policy");
  };

  const handleViewPolicies = () => {
    navigate("/revisr/policies");
  };

  // Add CSS animation styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes slideInLeft {
        from {
          opacity: 0;
          transform: translateX(-20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes slideInRight {
        from {
          opacity: 0;
          transform: translateX(20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!userIsLoading && !user) {
      navigate("/login");
    }
  }, [user, userIsLoading, navigate]);

  useEffect(() => {
    if (userIsLoading || !user?.id) return;

    let cancelled = false;
    let retryAttempts = 0;
    const MAX_RETRIES = 10;
    const RETRY_MS = 400;

    const fetchMetrics = async (orgId: string | null) => {
      if (cancelled) return;
      setIsMetricsLoading(true);
      try {
        const policies = await policyTeableService.getPolicies(user.id, orgId ?? undefined);
        if (cancelled) return;

        const metrics = policies.reduce<PolicyMetrics>(
          (acc, policy) => {
            const status = (policy.status || "draft").toLowerCase();
            acc.total += 1;
            if (status === "draft") { acc.draft += 1; return acc; }
            if (status === "completed" || status === "created") { acc.completed += 1; return acc; }
            acc.inProgress += 1;
            return acc;
          },
          { ...INITIAL_POLICY_METRICS },
        );

        setPolicyMetrics(metrics);
      } catch (error) {
        console.error("Error in fetchPolicyMetrics:", error);
        if (!cancelled) setPolicyMetrics(INITIAL_POLICY_METRICS);
      } finally {
        if (!cancelled) setIsMetricsLoading(false);
      }
    };

    // Retry until WorkspaceSwitcher writes the active workspace to localStorage.
    // On first login it sets localStorage asynchronously (after fetching memberships)
    // without firing "workspace-changed", so we poll until the value is present.
    const tryFetch = () => {
      if (cancelled) return;
      const orgId = getActiveWorkspaceId();
      if (orgId) {
        fetchMetrics(orgId);
        return;
      }
      if (retryAttempts < MAX_RETRIES) {
        retryAttempts++;
        setTimeout(tryFetch, RETRY_MS);
      } else {
        // Workspace never became available — fall back to user-scoped fetch
        fetchMetrics(null);
      }
    };

    tryFetch();

    // Re-fetch immediately when user manually switches workspace
    const handleWorkspaceChanged = () => {
      const orgId = getActiveWorkspaceId();
      fetchMetrics(orgId);
    };
    window.addEventListener("workspace-changed", handleWorkspaceChanged);

    return () => {
      cancelled = true;
      window.removeEventListener("workspace-changed", handleWorkspaceChanged);
    };
  }, [user?.id, userIsLoading]);

  // Show loading state while checking authentication
  if (userIsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <RevisrLayout>
      <PendingInvitePopup />
      <div className="container mx-auto px-4 py-5 md:px-6 md:py-8 scopingai-dashboard bg-background overflow-x-hidden">
        {/* Header */}
        <div
          className="flex items-start justify-between mb-6 p-4 md:p-6 bg-card rounded-xl border border-border shadow-sm"
          style={{ animation: "fadeInUp 0.6s ease-out" }}
        >
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
              <div className="w-2 h-7 md:h-8 bg-primary rounded-full flex-shrink-0"></div>
              Dashboard
            </h1>
            <p className="text-muted-foreground mt-2 ml-5 text-sm md:text-base">
              Welcome to Revisr. Boost your organisation policies with AI-powered guidance.
            </p>
          </div>
        </div>

        {/* Hero Section */}
        <Card
          className="mb-8 border border-border shadow-sm"
          style={{ animation: "fadeInUp 0.6s ease-out 0.2s both" }}
        >
          <CardContent className="p-4 md:p-6">
            <div className="space-y-4 md:space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-sm text-muted-foreground">
                <Sparkles className="h-4 w-4 text-primary" />
                Welcome to Revisr
              </div>
              <div>
                <h2 className="text-xl md:text-3xl font-bold text-foreground mb-2">
                  Boost your organisation policies
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                  Create stronger, compliant policies with AI support and structured templates.
                  Start your first policy workflow when you are ready.
                </p>
              </div>
              <div>
                <Button
                  onClick={handleStartWorkflow}
                  size="lg"
                  className="w-full sm:w-auto gap-2 px-5 py-4 text-sm font-semibold group"
                >
                  {policyMetrics.total > 0 ? "Get Started" : "Create First Policy"}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {!isMetricsLoading && policyMetrics.total > 0 && (
          <Card
            className="mb-8 border border-border shadow-sm"
            style={{ animation: "fadeInUp 0.6s ease-out 0.3s both" }}
          >
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
                <div>
                  <h3 className="text-lg md:text-xl font-semibold text-foreground">
                    Your Policies boosted with Revisr
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Live metrics from your policy workspace.
                  </p>
                </div>
                <Button
                  onClick={handleViewPolicies}
                  variant="outline"
                  className="w-full sm:w-auto sm:shrink-0"
                >
                  View Your Policies
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                    <FileText className="h-4 w-4 text-primary" />
                    Total Policies
                  </div>
                  <p className="text-2xl font-bold text-foreground">{policyMetrics.total}</p>
                </div>

                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    Completed
                  </div>
                  <p className="text-2xl font-bold text-foreground">{policyMetrics.completed}</p>
                </div>

                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                    <Clock3 className="h-4 w-4 text-primary" />
                    Draft / In Progress
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {policyMetrics.draft + policyMetrics.inProgress}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </RevisrLayout>
  );
}
